import { useEffect } from 'react';

/**
 * 活动归因参数（`?campaign=`）的全站捕获。
 *
 * 投放链接形如 `/activity/double-eleven?campaign=double-eleven`（后台「复制活动链接」给的就是这一形态），
 * 也可能落在注册页 `/register?campaign=double-eleven`。本 hook 只把它暂存进 localStorage 并从地址栏摘掉，
 * 真正的消费点在注册提交那一下（见 stores/registerStore 的 submitRegister）——
 * 与邀请码的 `?ref=` 是两条独立通路：那条要在登录后调 /referral/bind 建关系，这条不需要关系，
 * 只是「注册这一笔算在哪个活动头上」，由后端在落库那一刻按活动配置决定发不发、发多少。
 */

const PENDING_KEY = 'pending_campaign_key';

/** 活动标识的形状与后端 CAMPAIGN_KEY_PATTERN 一致；不合规的值直接丢弃，不发给后端挨一次校验 */
const KEY_PATTERN = /^[a-z0-9](?:[a-z0-9-]{1,48}[a-z0-9])?$/;

function normalize(raw: string | null): string | null {
  const key = raw?.trim().toLowerCase() ?? '';
  return KEY_PATTERN.test(key) ? key : null;
}

/** search 与 hash 两处都读（Web 用 BrowserRouter，其它形态留着不炸）；以 path 上的 /activity/:key 为准，见 detail 页 */
function readFromUrl(): string | null {
  const { search, hash } = window.location;
  const fromSearch = normalize(new URLSearchParams(search).get('campaign'));
  if (fromSearch) return fromSearch;
  const qIndex = hash.indexOf('?');
  if (qIndex >= 0) {
    return normalize(new URLSearchParams(hash.slice(qIndex + 1)).get('campaign'));
  }
  return null;
}

/** 只删 campaign 这一个参数，tab/checkout/ref 等深链参数原样保留 */
function stripFromUrl(): void {
  const { pathname, search, hash } = window.location;

  let nextSearch = search;
  const sp = new URLSearchParams(search);
  if (sp.has('campaign')) {
    sp.delete('campaign');
    const s = sp.toString();
    nextSearch = s ? `?${s}` : '';
  }

  let nextHash = hash;
  const qIndex = hash.indexOf('?');
  if (qIndex >= 0) {
    const hp = new URLSearchParams(hash.slice(qIndex + 1));
    if (hp.has('campaign')) {
      hp.delete('campaign');
      const hs = hp.toString();
      nextHash = hash.slice(0, qIndex) + (hs ? `?${hs}` : '');
    }
  }

  window.history.replaceState({}, '', `${pathname}${nextSearch}${nextHash}`);
}

export function readPendingCampaignKey(): string | null {
  try {
    return localStorage.getItem(PENDING_KEY);
  } catch {
    return null;
  }
}

export function writePendingCampaignKey(key: string): void {
  try {
    localStorage.setItem(PENDING_KEY, key);
  } catch {
    /* 隐私模式下 localStorage 不可用：归因丢了不影响注册成功，按最佳努力处理 */
  }
}

/** 注册成功后作废：这一段时长已经算清，留着会让下一个账号也挂在这个活动上 */
export function clearPendingCampaignKey(): void {
  try {
    localStorage.removeItem(PENDING_KEY);
  } catch {
    /* ignore */
  }
}

export function useCampaignCapture(): void {
  useEffect(() => {
    const fromUrl = readFromUrl();
    if (!fromUrl) return;
    writePendingCampaignKey(fromUrl);
    stripFromUrl();
  }, []);
}
